import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { marked } from 'marked';
import api from '../utils/api';
import { clearAuthSession } from '../utils/authStorage';

interface TaskGuide {
  id: string;
  slug: string;
  title: string;
  description?: string | null;
  content: string;
  updatedAt: string;
  pinned?: boolean;
  sortOrder?: number;
}

marked.setOptions({
  gfm: true,
  breaks: true,
});

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  margin: 0 0 0.5rem;
  color: ${({ theme }) => theme.text};
`;

const SubTitle = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.textSecondary};
  line-height: 1.6;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 1.5rem;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme.shadow};
`;

const Left = styled(Card)`
  padding: 1rem;
`;

const Right = styled(Card)`
  padding: 1.25rem 1.25rem 1.5rem;
  min-height: 420px;
`;

const Search = styled.input`
  width: 100%;
  padding: 0.75rem 0.9rem;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 10px;
  outline: none;
  font-size: 0.95rem;
  margin-bottom: 0.75rem;
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.text};

  &:focus {
    border-color: ${({ theme }) => theme.primary};
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.12);
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

const TabBtn = styled.button<{ $active?: boolean }>`
  flex: 1;
  border: 1px solid ${({ theme, $active }) => ($active ? theme.primary : theme.border)};
  background: ${({ theme, $active }) => ($active ? theme.surfaceAlt : theme.surface)};
  color: ${({ theme }) => theme.text};
  border-radius: 10px;
  padding: 0.55rem 0.65rem;
  cursor: pointer;
  font-weight: 700;

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
  }
`;

const Item = styled.button<{ $active?: boolean }>`
  width: 100%;
  text-align: left;
  border: 1px solid ${({ theme, $active }) => ($active ? theme.border : 'transparent')};
  background: ${({ theme, $active }) => ($active ? theme.surfaceAlt : 'transparent')};
  border-radius: 10px;
  padding: 0.75rem 0.75rem;
  cursor: pointer;
  transition: all 0.15s ease;
  margin-bottom: 0.5rem;

  &:hover {
    background: ${({ theme, $active }) => ($active ? theme.surfaceAlt : theme.surfaceAlt)};
  }
`;

const ItemTitle = styled.div`
  font-weight: 700;
  color: ${({ theme }) => theme.text};
  margin-bottom: 0.25rem;
`;

const ItemDesc = styled.div`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.9rem;
  line-height: 1.4;
`;

const Markdown = styled.div`
  color: ${({ theme }) => theme.text};
  line-height: 1.8;

  h1, h2, h3 {
    margin: 1.2rem 0 0.75rem;
  }
  h1 { font-size: 2rem; }
  h2 { font-size: 1.5rem; }
  h3 { font-size: 1.2rem; }

  p { margin: 0.8rem 0; }

  ul, ol {
    padding-left: 1.4rem;
    margin: 0.8rem 0;
  }
  li { margin: 0.3rem 0; }

  code {
    background: ${({ theme }) => theme.mutedBg};
    padding: 0.15rem 0.35rem;
    border-radius: 6px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 0.92em;
  }

  pre {
    background: #0b1020;
    color: #e6e6e6;
    padding: 1rem;
    border-radius: 12px;
    overflow: auto;
    margin: 1rem 0;
  }

  blockquote {
    margin: 1rem 0;
    padding: 0.6rem 0.9rem;
    border-left: 4px solid ${({ theme }) => theme.primary};
    background: ${({ theme }) => theme.surfaceAlt};
    color: ${({ theme }) => theme.textSecondary};
    border-radius: 8px;
  }
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.25rem;
`;

const UpdatedAt = styled.div`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.9rem;
`;

const TopActions = styled.div`
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin-top: 0.75rem;
`;

const Button = styled.button`
  padding: 0.55rem 0.85rem;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.text};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    box-shadow: ${({ theme }) => theme.shadow};
    transform: translateY(-1px);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 70;
`;

const ModalCard = styled.div`
  width: min(760px, 100%);
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.text};
  border-radius: 12px;
  padding: 1rem;
  box-shadow: ${({ theme }) => theme.hoverShadow};
`;

const Field = styled.label`
  display: grid;
  gap: 0.35rem;
  margin-top: 0.75rem;
  color: ${({ theme }) => theme.text};
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Input = styled.input`
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.text};
  border-radius: 10px;
  padding: 0.6rem 0.75rem;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.primary};
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.12);
  }
`;

const TextArea = styled.textarea`
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.text};
  border-radius: 10px;
  padding: 0.6rem 0.75rem;
  outline: none;
  min-height: 240px;
  resize: vertical;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;

  &:focus {
    border-color: ${({ theme }) => theme.primary};
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.12);
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  margin-top: 1rem;
  flex-wrap: wrap;
`;

function uuid() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function slugify(s: string) {
  return s
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const Empty = styled.div`
  color: ${({ theme }) => theme.textSecondary};
  padding: 2rem 0.25rem;
`;

const LOCAL_GUIDES_KEY = 'personal_blog.guides.local.v1';

function safeJsonParse<T>(s: string | null): T | null {
  if (!s) return null;
  try {
    return JSON.parse(s) as T;
  } catch {
    return null;
  }
}

function loadLocalGuides(): TaskGuide[] {
  const parsed = safeJsonParse<any>(localStorage.getItem(LOCAL_GUIDES_KEY));
  if (Array.isArray(parsed)) return parsed as TaskGuide[];
  return [];
}

function saveLocalGuides(list: TaskGuide[]) {
  localStorage.setItem(LOCAL_GUIDES_KEY, JSON.stringify(list));
}

const Guides: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [authTick, setAuthTick] = useState(0);
  const refreshAuth = () => setAuthTick((x) => x + 1);

  useEffect(() => {
    const handler = () => refreshAuth();
    window.addEventListener('today-auth-changed', handler as any);
    window.addEventListener('storage', handler as any);
    return () => {
      window.removeEventListener('today-auth-changed', handler as any);
      window.removeEventListener('storage', handler as any);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [loading, setLoading] = useState(true);
  const [guides, setGuides] = useState<TaskGuide[]>([]);
  const [systemGuides, setSystemGuides] = useState<TaskGuide[]>([]);
  const [active, setActive] = useState<TaskGuide | null>(null);
  const [q, setQ] = useState('');
  const [tab, setTab] = useState<'my' | 'lbj'>('lbj');

  const authedUser = useMemo(() => {
    const u = safeJsonParse<any>(localStorage.getItem('today_user'));
    return u?.id ? { id: Number(u.id), username: String(u.username ?? '') } : null;
  }, [authTick]);

  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editSlug, setEditSlug] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editErr, setEditErr] = useState<string | null>(null);
  const [editSaving, setEditSaving] = useState(false);

  const isLoggedIn = useMemo(() => !!localStorage.getItem('today_token'), [authTick]);
  const modeText = isLoggedIn ? `已登录：${authedUser?.username ?? ''}（云端）` : `游客（本地）`;

  // 清理“只剩用户信息但 token 不存在”的脏状态（否则会出现 UI/缓存错配）
  useEffect(() => {
    if (isLoggedIn) return;
    const hasUser = !!localStorage.getItem('today_user');
    if (hasUser) {
      clearAuthSession();
      refreshAuth();
      window.dispatchEvent(new Event('today-auth-changed'));
    }
  }, [isLoggedIn]);

  const load = async () => {
    setLoading(true);
    try {
      const query = q.trim() || undefined;
      // 系统内置原则（示例）：所有用户可读
      const sysRes = await api.get<TaskGuide[]>('/task-guides', { params: { q: query } });
      setSystemGuides(sysRes.data ?? []);

      // 个人原则：登录走云端，游客走本地
      if (isLoggedIn) {
        const res = await api.get<TaskGuide[]>('/user-guides', { params: { q: query } });
        setGuides(res.data ?? []);
        return;
      }
      const local = loadLocalGuides();
      const filtered = query
        ? local.filter((g) =>
            `${g.title} ${g.slug} ${g.description ?? ''} ${g.content}`.toLowerCase().includes(query.toLowerCase()),
          )
        : local;
      setGuides(filtered);
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditId(null);
    setEditTitle('');
    setEditSlug('');
    setEditDesc('');
    setEditContent('');
    setEditErr(null);
    setEditOpen(true);
  };

  const openEdit = (g: TaskGuide) => {
    if (g.slug.startsWith('sys-')) {
      setEditErr('系统内置原则为只读，请在后台（admin）管理。');
      return;
    }
    setEditId(g.id);
    setEditTitle(g.title);
    setEditSlug(g.slug);
    setEditDesc(g.description ?? '');
    setEditContent(g.content);
    setEditErr(null);
    setEditOpen(true);
  };

  const saveGuide = async () => {
    const title = editTitle.trim();
    const slug2 = editSlug.trim();
    const content = editContent;
    const desc2 = editDesc.trim();

    if (!title) return setEditErr('标题不能为空。');
    if (!slug2) return setEditErr('slug 不能为空。');
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug2)) return setEditErr('slug 必须为小写字母/数字，单词间用 - 连接。');
    if (slug2.startsWith('sys-')) return setEditErr('sys- 为系统内置保留前缀，请换一个。');
    if (!content.trim()) return setEditErr('内容不能为空。');

    setEditSaving(true);
    setEditErr(null);
    try {
      if (isLoggedIn) {
        if (editId) {
          await api.put(`/user-guides/${encodeURIComponent(editId)}`, {
            title,
            slug: slug2,
            description: desc2 || null,
            content,
          });
        } else {
          await api.post('/user-guides', {
            title,
            slug: slug2,
            description: desc2 || null,
            content,
          });
        }
        setEditOpen(false);
        await load();
        navigate(`/guides/${slug2}`);
        return;
      }

      // guest local
      const local = loadLocalGuides();
      const nowIso = new Date().toISOString();
      const hasDup = local.some((g) => g.slug === slug2 && g.id !== editId);
      if (hasDup) {
        setEditErr('本地已存在相同 slug 的原则，请换一个。');
        return;
      }
      let next: TaskGuide[];
      if (editId) {
        next = local.map((g) =>
          g.id === editId
            ? { ...g, title, slug: slug2, description: desc2 || null, content, updatedAt: nowIso }
            : g,
        );
      } else {
        next = [
          ...local,
          {
            id: uuid(),
            title,
            slug: slug2,
            description: desc2 || null,
            content,
            updatedAt: nowIso,
          },
        ];
      }
      saveLocalGuides(next);
      setEditOpen(false);
      await load();
      navigate(`/guides/${slug2}`);
    } catch (e: any) {
      if (e?.response?.status === 401) setEditErr('未登录或登录已过期。');
      else setEditErr('保存失败（可能 slug 冲突）。');
    } finally {
      setEditSaving(false);
    }
  };

  const deleteGuide = async (g: TaskGuide) => {
    if (g.slug.startsWith('sys-')) {
      alert('系统内置原则为只读，请在后台（admin）管理。');
      return;
    }
    if (!confirm(`确定删除「${g.title}」吗？`)) return;
    try {
      if (isLoggedIn) {
        await api.delete(`/user-guides/${encodeURIComponent(g.id)}`);
        await load();
      } else {
        const local = loadLocalGuides();
        const next = local.filter((x) => x.id !== g.id);
        saveLocalGuides(next);
        await load();
      }
      if (active?.id === g.id) {
        navigate('/guides');
        setActive(null);
      }
    } catch {
      alert('删除失败。');
    }
  };

  // 登录状态变化后（例如从 Today 登录后跳转过来），自动刷新一次列表，避免仍显示旧数据
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  useEffect(() => {
    const t = setTimeout(() => load(), 250);
    return () => clearTimeout(t);
  }, [q]);

  const mergedGuides = useMemo(() => {
    // 系统示例在前，个人在后；slug 天然隔离（sys- vs user）
    const map = new Map<string, TaskGuide>();
    for (const g of [...systemGuides, ...guides]) {
      if (g?.slug) map.set(g.slug, g);
    }
    return Array.from(map.values());
  }, [systemGuides, guides]);

  const listForTab = useMemo(() => {
    return tab === 'lbj' ? systemGuides : guides;
  }, [tab, systemGuides, guides]);

  useEffect(() => {
    if (slug) {
      const match = mergedGuides.find((g) => g.slug === slug);
      if (match) {
        setActive(match);
        setTab(match.slug.startsWith('sys-') ? 'lbj' : 'my');
        return;
      }
    }

    // 没有 slug：不要“强行跳到另一个标签页”，而是尊重用户当前 tab。
    // 另外：如果当前 active 已被删除（不在 mergedGuides），需要清掉或重选同 tab 的第一个。
    const activeSlug = active?.slug;
    const stillExists = activeSlug ? mergedGuides.some((g) => g.slug === activeSlug) : false;
    if (stillExists) return;

    const pick = tab === 'lbj' ? systemGuides[0] : guides[0];
    if (pick) {
      setActive(pick);
      return;
    }

    // 当前 tab 已空：保持 tab，不自动切到另一个 tab
    setActive(null);
  }, [mergedGuides, slug, tab, guides, systemGuides, active?.slug]);

  const html = useMemo(() => {
    if (!active?.content) return '';
    return marked.parse(active.content) as string;
  }, [active]);

  return (
    <Container>
      {editOpen ? (
        <ModalOverlay
          onClick={() => {
            if (editSaving) return;
            setEditOpen(false);
          }}
        >
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: 900 }}>{editId ? '编辑原则' : '新建原则'}</div>
            <div style={{ color: 'inherit', opacity: 0.75, marginTop: '0.25rem' }}>
              slug 会用于 Today 绑定与 URL（例如：/guides/fitness）
            </div>

            <FieldRow>
              <Field>
                <div>标题</div>
                <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
              </Field>
              <Field>
                <div>slug</div>
                <Input
                  value={editSlug}
                  onChange={(e) => setEditSlug(e.target.value)}
                  onBlur={() => setEditSlug((s) => slugify(s || editTitle))}
                  placeholder="小写字母/数字/-"
                />
              </Field>
            </FieldRow>

            <Field>
              <div>简介（可选）</div>
              <Input value={editDesc} onChange={(e) => setEditDesc(e.target.value)} />
            </Field>

            <Field>
              <div>内容（Markdown）</div>
              <TextArea value={editContent} onChange={(e) => setEditContent(e.target.value)} />
            </Field>

            {editErr ? <Empty style={{ padding: '0.75rem 0.25rem' }}>{editErr}</Empty> : null}

            <ModalActions>
              <Button onClick={() => setEditOpen(false)} disabled={editSaving}>
                取消
              </Button>
              <Button onClick={() => void saveGuide()} disabled={editSaving}>
                {editSaving ? '保存中...' : '保存'}
              </Button>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      ) : null}

      <Header>
        <Title>任务原则</Title>
        <SubTitle>
          选择一个任务，在执行时快速回顾「原则 / 注意事项」，避免重心偏移。
        </SubTitle>
        <TopActions>
          <Button title="登录后可云端保存；游客则本地保存">
            {modeText}
          </Button>
          <Button
            onClick={() => {
              openNew();
            }}
          >
            新建原则
          </Button>
        </TopActions>
      </Header>

      <Grid>
        <Left>
          <Search
            placeholder="搜索任务（title / slug / 简介）"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />

          {loading ? (
            <Empty>加载中...</Empty>
          ) : mergedGuides.length === 0 ? (
            <Empty>暂无内容。你可以注册/登录后在前台新增；或游客模式先本地使用。</Empty>
          ) : (
            <>
              <Tabs>
                <TabBtn
                  $active={tab === 'my'}
                  onClick={() => {
                    setTab('my');
                    if (!active || active.slug.startsWith('sys-')) {
                      if (guides[0]) {
                        setActive(guides[0]);
                        navigate(`/guides/${guides[0].slug}`);
                      } else {
                        setActive(null);
                        navigate('/guides');
                      }
                    }
                  }}
                >
                  你的原则（{guides.length}）
                </TabBtn>
                <TabBtn
                  $active={tab === 'lbj'}
                  onClick={() => {
                    setTab('lbj');
                    if (!active || !active.slug.startsWith('sys-')) {
                      if (systemGuides[0]) {
                        setActive(systemGuides[0]);
                        navigate(`/guides/${systemGuides[0].slug}`);
                      } else {
                        setActive(null);
                        navigate('/guides');
                      }
                    }
                  }}
                >
                  LBJ 的原则（{systemGuides.length}）
                </TabBtn>
              </Tabs>

              {(listForTab.length ? listForTab : []).map((g) => (
              <Item
                key={g.id}
                $active={active?.id === g.id}
                onClick={() => {
                  setActive(g);
                  navigate(`/guides/${g.slug}`);
                }}
              >
                <ItemTitle>{g.title}</ItemTitle>
                <ItemDesc>{g.description || g.slug}</ItemDesc>
              </Item>
              ))}
              {!listForTab.length ? (
                <Empty style={{ padding: '1.2rem 0.25rem' }}>
                  {tab === 'lbj' ? '暂无系统原则。' : '暂无你的原则。'}
                </Empty>
              ) : null}
            </>
          )}
        </Left>

        <Right>
          {!active ? (
            <Empty>请选择一个任务。</Empty>
          ) : (
            <>
              <MetaRow>
                <h2 style={{ margin: 0 }}>{active.title}</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                  <UpdatedAt>更新于 {new Date(active.updatedAt).toLocaleString()}</UpdatedAt>
                  <Button
                    onClick={() => {
                      openEdit(active);
                    }}
                    disabled={active.slug.startsWith('sys-')}
                    style={active.slug.startsWith('sys-') ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                  >
                    编辑
                  </Button>
                  <Button
                    onClick={() => {
                      void deleteGuide(active);
                    }}
                    disabled={active.slug.startsWith('sys-')}
                    style={active.slug.startsWith('sys-') ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                  >
                    删除
                  </Button>
                </div>
              </MetaRow>
              {active.description ? (
                <div style={{ color: 'inherit', opacity: 0.75, marginBottom: '1rem' }}>{active.description}</div>
              ) : null}
              <Markdown dangerouslySetInnerHTML={{ __html: html }} />
            </>
          )}
        </Right>
      </Grid>
    </Container>
  );
};

export default Guides;

